import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import SubTitleNew from "../common/New/SubTitleNew";
import StatsCard from "../common/New/StatsCard";
import DynamicTable, { TableStatusBadge } from "../common/New/DynamicTable";
import {
  getAllTrackingDevices,
  getTrackingDevicesByOrgIdIndependent,
  deleteTrackingDevice,
  getDeviceMonitorAlerts,
  getDeviceMonitorLatest,
  getDeviceHistory,
  getDeviceStreamUrl,
} from "./deviceApi";
import CreateDevice from "./CreateDevice";
import DeleteModal from "../common/DeleteModal";
import "./DeviceList.css";
import { Box, IconButton } from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SpeedIcon from "@mui/icons-material/Speed";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import CompressIcon from "@mui/icons-material/Compress";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import RouterIcon from "@mui/icons-material/Router";
import SpeakerIcon from "@mui/icons-material/Speaker";
import MicIcon from "@mui/icons-material/Mic";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import TvIcon from "@mui/icons-material/Tv";
import SensorsIcon from "@mui/icons-material/Sensors";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import axios from "axios";
import config from "../../config";
import { downloadBackendExcel, exportTableToCSV } from "../../utils/exportTableData";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import {
  getLayoutSection,
  getVisibleLayoutColumns,
  getVisibleLayoutWidgets,
} from "../../layout/moduleLayoutUtils";
import {
  ActiveDevicesStatsIcon,
  AvgUptimeStatsIcon,
  DeviceIssuesStatsIcon,
  InstalledDevicesStatsIcon,
  SimExpiryStatsIcon,
  TotalDevicesStatsIcon,
} from "./deviceStatsIcons";
import {
  isInactiveState,
  isSensorAlert,
  isSensorNearThreshold,
} from "../../utils/deviceStatusHelpers";

const CHART_COLORS = ["#3B82F6", "#A855F7", "#22C55E", "#F59E0B", "#20A8E0", "#94A3B8"];
const OWNER_COLORS = ["#20A8E0", "#16A34A", "#FACC15", "#F97316", "#94A3B8", "#6366F1"];
const DEVICE_TABLE_BATCH_SIZE = 10;
const DEFAULT_DATE_RANGE = {
  type: "preset",
  label: "Today",
  value: "today",
};
const DEVICE_DATE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "last_7_days" },
  { label: "Last 30 Days", value: "last_30_days" },
];
const HISTORY_SUPPORTED_KEYS = new Set([
  "HUMIDITY",
  "TEMPERATURE",
  "FUEL",
  "FUEL_LEVEL",
  "DASH_CAMERA",
  "SPEAKER",
  "VIBRATION",
  "SMOKE",
  "PRESSURE",
  "BATTERY_VOLTAGE",
  "IGNITION",
  "MICROPHONE",
  "CCTV",
  "RFID_READER",
  "GPS",
  "GPS_TRACKER",
  "TELEVISION",
]);

const ALERT_TYPES = [
  { key: "HUMIDITY", label: "Humidity", icon: WaterDropIcon, matches: ["HUMIDITY"] },
  { key: "TEMPERATURE", label: "Temperature", icon: DeviceThermostatIcon, matches: ["TEMPERATURE"] },
  { key: "PRESSURE", label: "Pressure", icon: CompressIcon, matches: ["PRESSURE"] },
  { key: "BATTERY", label: "Battery", icon: BatteryAlertIcon, matches: ["BATTERY", "BATTERY_VOLTAGE"] },
  { key: "FUEL", label: "Fuel", icon: LocalGasStationIcon, matches: ["FUEL", "FUEL_LEVEL"] },
  { key: "SMOKE", label: "Smoke", icon: LocalFireDepartmentIcon, matches: ["SMOKE"] },
  { key: "VIBRATION", label: "Vibration", icon: SensorsIcon, matches: ["VIBRATION"] },
  { key: "CCTV", label: "CCTV", icon: VideocamOffIcon, matches: ["CCTV", "CAMERA", "DASH_CAMERA"] },
  { key: "GPS", label: "GPS", icon: GpsFixedIcon, matches: ["GPS", "GPS_TRACKER"] },
  { key: "RFID", label: "RFID", icon: RouterIcon, matches: ["RFID", "RFID_READER"] },
  { key: "SPEAKER", label: "Speaker", icon: SpeakerIcon, matches: ["SPEAKER"] },
  { key: "MICROPHONE", label: "Microphone", icon: MicIcon, matches: ["MICROPHONE", "MIC"] },
  { key: "TELEVISION", label: "Television", icon: TvIcon, matches: ["TELEVISION", "TV", "SMART_TV"] },
  { key: "IGNITION", label: "Ignition", icon: SpeedIcon, matches: ["IGNITION"] },
  { key: "OVER_SPEED", label: "Over Speed", icon: SpeedIcon, matches: ["OVER_SPEED", "OVERSPEED", "SPEED"] },
];

const DEVICE_STAT_ICON_BY_METRIC = {
  totalDevices: TotalDevicesStatsIcon,
  installedDevices: InstalledDevicesStatsIcon,
  activeDevices: ActiveDevicesStatsIcon,
  devicesWithIssues: DeviceIssuesStatsIcon,
  simExpiryNext30Days: SimExpiryStatsIcon,
  avgUptime: AvgUptimeStatsIcon,
};

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatAlertNumber = (value) => {
  const number = toFiniteNumber(value);
  if (number === null) return "";
  return Number.isInteger(number)
    ? String(number)
    : number.toFixed(2).replace(/\.?0+$/, "");
};

const normalizeHistoryKey = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

const getDeviceAlertTypeDefinition = (...values) => {
  const normalizedValues = values
    .flat()
    .filter(Boolean)
    .map(normalizeHistoryKey);

  return (
    ALERT_TYPES.find((type) =>
      normalizedValues.some((value) =>
        type.matches.some((match) => value.includes(normalizeHistoryKey(match))),
      ),
    ) || null
  );
};

const getAlertTrackingDevice = (alert) =>
  alert?.trackingDevice || alert?.device || alert?.TrackingDevice || {};

const getAlertDeviceTypeDefinition = (alert) => {
  const trackingDevice = getAlertTrackingDevice(alert);
  const deviceType =
    typeof trackingDevice?.deviceType === "object"
      ? trackingDevice.deviceType?.name
      : trackingDevice?.deviceType;

  return (
    getDeviceAlertTypeDefinition(
      alert?.sensorType,
      trackingDevice?.sensorType,
      deviceType,
      alert?.deviceType,
    ) ||
    getDeviceAlertTypeDefinition(
      alert?.type,
      alert?.alertType,
      alert?.category,
      alert?.name,
      alert?.message,
      alert?.description,
    ) ||
    { key: "ALERT", label: "Alert", icon: ErrorOutlineIcon, matches: ["ALERT"] }
  );
};

const getDeviceTypeDefinition = (device) =>
  getDeviceAlertTypeDefinition(device?.sensorType, device?.deviceType, device?.type);

const getAlertDeviceMatch = (alert, devices = []) => {
  const trackingDevice = getAlertTrackingDevice(alert);
  const alertKeys = [
    alert?.trackingDeviceId,
    alert?.deviceId,
    trackingDevice?.id,
    trackingDevice?.trackingDeviceId,
    trackingDevice?.deviceId,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(String);

  return devices.find((device) =>
    [
      device?.id,
      device?.trackingDeviceId,
      device?.deviceId,
    ]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map(String)
      .some((key) => alertKeys.includes(key)),
  );
};

const getAlertThresholdCopy = (alert, matchedDevice) => {
  const trackingDevice = getAlertTrackingDevice(alert);
  const value = toFiniteNumber(
    alert?.currentValue ??
      alert?.value ??
      alert?.reading ??
      matchedDevice?.value,
  );
  const min = toFiniteNumber(
    alert?.minValue ?? alert?.min ?? trackingDevice?.minValue ?? matchedDevice?.minValue,
  );
  const max = toFiniteNumber(
    alert?.maxValue ?? alert?.max ?? trackingDevice?.maxValue ?? matchedDevice?.maxValue,
  );
  const explicitThreshold = toFiniteNumber(alert?.thresholdValue ?? alert?.threshold);
  const alertKey = normalizeHistoryKey(
    `${alert?.alertType || ""} ${alert?.type || ""} ${alert?.status || ""}`,
  );
  const unit = alert?.unit || trackingDevice?.unit || matchedDevice?.unit || "";

  if (value === null) {
    return {
      detail: "",
      valueText: alert?.value || alert?.reading || alert?.status || "",
    };
  }

  const isLow =
    alertKey.includes("LOW") ||
    (explicitThreshold !== null && value < explicitThreshold && !alertKey.includes("HIGH")) ||
    (min !== null && value < min && !(max !== null && value > max));
  const isHigh =
    alertKey.includes("HIGH") ||
    alertKey.includes("EXCEED") ||
    (explicitThreshold !== null && value > explicitThreshold) ||
    (max !== null && value > max);
  const threshold =
    explicitThreshold ??
    (isLow ? min : isHigh ? max : value < (min ?? value) ? min : max);

  if (threshold === null) {
    return { detail: "", valueText: `${formatAlertNumber(value)}${unit}` };
  }

  const difference = isLow ? threshold - value : value - threshold;
  if (!Number.isFinite(difference) || difference <= 0) {
    return {
      detail: "",
      valueText: `${formatAlertNumber(value)}${unit}`,
    };
  }

  return {
    detail: `${isLow ? "Below" : "Exceeded"} by ${formatAlertNumber(difference)}${unit}`,
    valueText: `${formatAlertNumber(value)}${unit}`,
  };
};

const formatDeviceTypeLabel = (value) => {
  if (!value) return "-";
  const normalized = String(value).trim().toUpperCase().replace(/[\s_]+/g, "_");
  const aliases = {
    VIBRATON: "VIBRATION",
    VIBRATION_SENSOR: "VIBRATION",
    HUMIDITY_SENSOR: "HUMIDITY",
    TEMPERATURE_SENSOR: "TEMPERATURE",
    MICROPHONE_SENSOR: "MICROPHONE",
    MICROPHE: "MICROPHONE",
  };

  return String(aliases[normalized] || value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bGps\b/g, "GPS")
    .replace(/\bRfid\b/g, "RFID")
    .replace(/\bTv\b/g, "TV")
    .replace(/\bCctv\b/g, "CCTV");
};

const getDeviceTypeName = (device) =>
  typeof device?.deviceType === "object"
    ? device.deviceType?.name || ""
    : device?.deviceType || "";

const getAssignedVehicle = (device) =>
  device?.assignedVehicle || device?.vehicle || device?.Vehicle || null;

const getAssignedStop = (device) =>
  device?.assignedStop || device?.stop || device?.Stop || null;

const getDeviceOrganization = (device) =>
  device?.organization ||
  getAssignedVehicle(device)?.organization ||
  getAssignedStop(device)?.organization ||
  null;

const getInstalledOrganizationName = (device) =>
  device?.organization?.name || device?.organizationName || "Unassigned";

const buildSiteLookup = (sites = []) => {
  const siteLookup = new Map();
  sites.forEach((site) => {
    [site?.id, site?.siteId]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .forEach((value) => siteLookup.set(String(value), site));
  });
  return siteLookup;
};

const getDeviceOrganizationId = (device = {}) =>
  device?.organizationId ||
  device?.organization?.id ||
  getAssignedVehicle(device)?.organizationId ||
  getAssignedVehicle(device)?.organization?.id ||
  getAssignedStop(device)?.organizationId ||
  getAssignedStop(device)?.organization?.id ||
  "";

const getUniqueDeviceOrganizationIds = (devices = []) =>
  Array.from(
    new Set(
      devices
        .map(getDeviceOrganizationId)
        .filter(Boolean)
        .map(String),
    ),
  );

const getLatestReadingKeyCandidates = (reading = {}) =>
  [
    reading.trackingDeviceId,
    reading.id,
    reading.deviceId,
    reading.deviceExternalId,
  ]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(String);

const buildLatestReadingLookup = (readings = []) => {
  const lookup = new Map();
  readings.forEach((reading) => {
    getLatestReadingKeyCandidates(reading).forEach((key) => {
      lookup.set(key, reading);
    });
  });
  return lookup;
};

const mergeDeviceWithLatestReading = (device = {}, latestReading = {}) => {
  if (!latestReading || !Object.keys(latestReading).length) return device;

  return {
    ...device,
    value: latestReading.value ?? device.value,
    status: latestReading.status ?? device.status,
    health: latestReading.health ?? device.health,
    unit: latestReading.unit ?? device.unit,
    minValue: latestReading.minValue ?? device.minValue,
    maxValue: latestReading.maxValue ?? device.maxValue,
    warningMargin: latestReading.warningMargin ?? device.warningMargin,
    deviceInactiveMinutes:
      latestReading.deviceInactiveMinutes ?? device.deviceInactiveMinutes,
    recordedAt: latestReading.recordedAt ?? device.recordedAt,
    historyId: latestReading.historyId ?? device.historyId,
    messageId: latestReading.messageId ?? device.messageId,
  };
};

const getResolvedSite = (siteId, device, vehicle, stop, siteLookup) =>
  device?.site ||
  vehicle?.site ||
  stop?.site ||
  siteLookup?.get(String(siteId || "")) ||
  null;

const getResolvedSiteName = (site, device, siteId) =>
  site?.name ||
  site?.siteName ||
  device?.siteName ||
  site?.siteId ||
  (siteId ? String(siteId) : "");

const normalizeDeviceForList = (device, siteLookup = new Map()) => {
  const vehicle = getAssignedVehicle(device);
  const stop = getAssignedStop(device);
  const organization = getDeviceOrganization(device);
  const siteId =
    device?.siteId ||
    device?.site?.id ||
    device?.site?.siteId ||
    vehicle?.siteId ||
    vehicle?.site?.id ||
    vehicle?.site?.siteId ||
    stop?.siteId ||
    stop?.site?.id ||
    stop?.site?.siteId ||
    "";
  const site = getResolvedSite(siteId, device, vehicle, stop, siteLookup);
  const siteName = getResolvedSiteName(site, device, siteId);

  return {
    ...device,
    deviceType: getDeviceTypeName(device),
    vehicle,
    stop,
    siteId,
    siteName,
    site,
    organization,
    organizationName: device?.organizationName || organization?.name || "",
    vehicleName:
      device?.vehicleName ||
      vehicle?.name ||
      vehicle?.VehicleNumber ||
      "",
    stopName: device?.stopName || stop?.name || "",
  };
};

const getDeviceDisplayType = (device) => {
  if (!device?.deviceType) return "Others";
  if (device.deviceType.toUpperCase() === "SENSOR" && device?.sensorType) {
    return formatDeviceTypeLabel(device.sensorType);
  }
  return formatDeviceTypeLabel(device.deviceType);
};

const getHistoryKey = (device) => {
  const sensorKey = normalizeHistoryKey(device?.sensorType);
  if (HISTORY_SUPPORTED_KEYS.has(sensorKey)) return sensorKey;

  const typeKey = normalizeHistoryKey(device?.deviceType);
  if (HISTORY_SUPPORTED_KEYS.has(typeKey)) return typeKey;

  return null;
};

const getStartOfDay = (date = new Date()) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const getDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getHourKey = (date) =>
  `${getDateKey(date)}T${String(date.getHours()).padStart(2, "0")}`;

const getInclusiveTrendHours = (dateValue) => {
  const start = getStartOfDay(dateValue);
  if (Number.isNaN(start.getTime())) {
    return getInclusiveTrendHours(new Date());
  }

  return Array.from({ length: 24 }, (_, index) => {
    const date = new Date(start);
    date.setHours(index, 0, 0, 0);
    const endDate = new Date(date);
    endDate.setMinutes(59, 59, 999);

    return {
      date,
      endDate,
      key: getHourKey(date),
      day: `${String(index).padStart(2, "0")}:00`,
      bucketType: "hour",
    };
  });
};

const getInclusiveTrendDays = (startDate, endDate) => {
  const start = getStartOfDay(startDate);
  const end = getStartOfDay(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return getInclusiveTrendDays(new Date(), new Date());
  }

  const totalDays = Math.round((end - start) / 86400000) + 1;
  return Array.from({ length: totalDays }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      date,
      endDate: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
      key: getDateKey(date),
      day: date.toLocaleDateString("en-US", {
        month: totalDays > 7 ? "short" : undefined,
        day: totalDays > 7 ? "numeric" : undefined,
        weekday: totalDays <= 7 ? "short" : undefined,
      }),
      bucketType: "day",
    };
  });
};

const getTrendDays = (dateRange = DEFAULT_DATE_RANGE) => {
  const today = getStartOfDay();
  const value = dateRange?.value || dateRange?.label || "today";

  switch (value) {
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return getInclusiveTrendHours(yesterday);
    }
    case "today":
      return getInclusiveTrendHours(today);
    case "last_7_days": {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return getInclusiveTrendDays(start, today);
    }
    case "last_30_days": {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      return getInclusiveTrendDays(start, today);
    }
    case "this_week": {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      return getInclusiveTrendDays(start, today);
    }
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return getInclusiveTrendDays(start, today);
    }
    default:
      break;
  }

  if (dateRange?.type === "custom" && dateRange.startDate && dateRange.endDate) {
    return getInclusiveTrendDays(dateRange.startDate, dateRange.endDate);
  }

  return getInclusiveTrendDays(today, today);
};

const getTrendBucketKey = (value, bucketType = "day") => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  if (bucketType === "hour") {
    return getHourKey(date);
  }

  return getDateKey(getStartOfDay(date));
};

const getDateRangeParams = (dateRange = DEFAULT_DATE_RANGE) => {
  const buckets = getTrendDays(dateRange);
  if (!buckets.length) return {};

  return {
    from: buckets[0].date.toISOString(),
    to: buckets[buckets.length - 1].endDate.toISOString(),
  };
};

const isDateWithinRange = (value, rangeParams = {}) => {
  if (!value) return true;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return true;

  const fromTime = rangeParams.from ? new Date(rangeParams.from).getTime() : null;
  const toTime = rangeParams.to ? new Date(rangeParams.to).getTime() : null;

  if (fromTime !== null && time < fromTime) return false;
  if (toTime !== null && time > toTime) return false;
  return true;
};

const getStoredAccessToken = () => {
  try {
    return localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || "";
  } catch {
    return "";
  }
};

const parseSseBlock = (block) => {
  const data = block
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.replace(/^data:\s?/, ""))
    .join("\n")
    .trim();

  if (!data) return null;
  return JSON.parse(data);
};

const getDeviceAlertRecordKey = (alert = {}) =>
  String(
    alert.id ||
      alert.deviceAlertId ||
      alert.alertId ||
      [
        alert.trackingDeviceId || alert.trackingDevice?.id,
        alert.alertType || alert.type || alert.sensorType || alert.deviceType,
        alert.vehicleId || alert.stopId || "",
        alert.createdAt || alert.recordedAt || alert.updatedAt || "",
      ]
        .filter(Boolean)
        .join("-"),
  );

const getStreamAlertSeverity = (payload = {}) => {
  const status = normalizeHistoryKey(payload.status || payload.health || payload.severity);
  if (["WARNING", "WARN"].includes(status)) return "WARNING";
  if (
    ["ALERT", "CRITICAL", "INACTIVE", "OFF", "NOT_WORKING", "ERROR", "FAULT", "DISABLED"].includes(
      status,
    )
  ) {
    return "CRITICAL";
  }

  const value = Number(payload.value);
  const min = Number(payload.minValue);
  const max = Number(payload.maxValue);
  const warningMargin = Number(payload.warningMargin);
  if (Number.isFinite(value)) {
    if (Number.isFinite(min) && value < min) return "CRITICAL";
    if (Number.isFinite(max) && value > max) return "CRITICAL";
    if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
      const margin = Number.isFinite(warningMargin) && warningMargin >= 0 ? warningMargin : 5;
      const warningDistance = margin;
      if (value - min <= warningDistance || max - value <= warningDistance) {
        return "WARNING";
      }
    }
  }

  return null;
};

const buildDeviceListStreamAlert = (payload = {}) => {
  const sourceAlert = payload?.alert && typeof payload.alert === "object" ? payload.alert : null;
  const severity = sourceAlert?.severity || getStreamAlertSeverity(payload);
  if (!sourceAlert && !severity) return null;

  const createdAt = sourceAlert?.createdAt || payload.recordedAt || new Date().toISOString();
  const alertType =
    sourceAlert?.alertType ||
    sourceAlert?.type ||
    payload.sensorType ||
    payload.deviceType ||
    "DEVICE_ALERT";

  return {
    ...sourceAlert,
    id:
      sourceAlert?.id ||
      sourceAlert?.deviceAlertId ||
      sourceAlert?.alertId ||
      `${payload.trackingDeviceId || payload.deviceId}-${alertType}-${createdAt}`,
    trackingDeviceId: sourceAlert?.trackingDeviceId || payload.trackingDeviceId,
    deviceId: sourceAlert?.deviceId || payload.deviceExternalId || payload.deviceId,
    deviceType: sourceAlert?.deviceType || payload.deviceType,
    sensorType: sourceAlert?.sensorType || payload.sensorType,
    value: sourceAlert?.value ?? payload.value,
    status: sourceAlert?.status || payload.status || severity,
    unit: sourceAlert?.unit || payload.unit,
    organizationId: sourceAlert?.organizationId || payload.organizationId,
    vehicleId: sourceAlert?.vehicleId || payload.vehicleId,
    stopId: sourceAlert?.stopId || payload.stopId,
    vehicleName: sourceAlert?.vehicleName || payload.vehicleName,
    stopName: sourceAlert?.stopName || payload.stopName,
    alertType,
    severity,
    message: sourceAlert?.message || `${payload.deviceExternalId || payload.deviceId || "Device"} alert`,
    createdAt,
    updatedAt: sourceAlert?.updatedAt || payload.recordedAt || createdAt,
    trackingDevice: {
      ...(sourceAlert?.trackingDevice || {}),
      id: sourceAlert?.trackingDevice?.id || payload.trackingDeviceId,
      deviceId:
        sourceAlert?.trackingDevice?.deviceId || payload.deviceExternalId || payload.deviceId,
      sensorType: sourceAlert?.trackingDevice?.sensorType || payload.sensorType,
      deviceType: sourceAlert?.trackingDevice?.deviceType || payload.deviceType,
    },
    vehicle:
      sourceAlert?.vehicle ||
      (payload.vehicleName ? { id: payload.vehicleId, name: payload.vehicleName } : undefined),
    stop:
      sourceAlert?.stop ||
      (payload.stopName ? { id: payload.stopId, name: payload.stopName } : undefined),
  };
};

const buildDistribution = (items, getLabel, limit = 5) => {
  const counts = items.reduce((acc, item) => {
    const rawLabel = String(getLabel(item) || "").trim();
    const label =
      rawLabel && rawLabel !== "-"
        ? ["other", "others"].includes(rawLabel.toLowerCase())
          ? "Others"
          : rawLabel
        : "Others";
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});

  const entries = Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const finalEntries = entries.slice(0, limit).map((item) => ({ ...item }));
  const remaining = entries.slice(limit).reduce((sum, item) => sum + item.value, 0);
  if (remaining) {
    const existingOthers = finalEntries.find((item) => item.name === "Others");
    if (existingOthers) {
      existingOthers.value += remaining;
    } else {
      finalEntries.push({ name: "Others", value: remaining });
    }
  }
  const total = items.length || 0;

  return finalEntries.map((item) => ({
    ...item,
    displayValue: item.value,
    displayText: `${item.value} (${total ? Math.round((item.value / total) * 100) : 0}%)`,
  }));
};

const getUiStatus = (device) => {
  if (isInactiveState(device)) return "Inactive";
  if (isSensorAlert(device)) return "Inactive";
  if (isSensorNearThreshold(device)) return "Warning";
  return device?.workingCondition === "WORKING" ? "Active" : "Inactive";
};

const getStatusBadgeTone = (status) => {
  if (status === "Active") return "green";
  if (status === "Warning") return "amber";
  return "red";
};

const getAttachedTo = (device) =>
  device?.vehicle?.name ||
  device?.stop?.name ||
  device?.vehicleName ||
  device?.stopName ||
  "-";

function DonutAnalyticsCard({ title, data, total, colors = CHART_COLORS }) {
  const { t } = useTranslation();
  const safeData = data.length ? data : [{ name: "No Data", value: 1, displayValue: 0 }];

  return (
    <div className="device-analytics-card device-donut-card">
      <h3>{t(title)}</h3>
      <div className="device-donut-content">
        <div className="device-donut-chart">
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie
                data={safeData}
                dataKey="value"
                innerRadius={44}
                outerRadius={64}
                paddingAngle={1}
                stroke="none"
              >
                {safeData.map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={data.length ? colors[index % colors.length] : "#E2E8F0"}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="device-donut-total">
            <strong>{total}</strong>
            <span>{t("Total")}</span>
          </div>
        </div>
        <div className="device-chart-legend">
          {data.map((item, index) => (
            <div className="device-chart-legend-row" key={item.name}>
              <span>
                <i style={{ backgroundColor: colors[index % colors.length] }} />
                {t(item.name, { defaultValue: item.name })}
              </span>
              <strong>{item.displayText}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DeviceBarListCard({ title, data, showViewAll = false }) {
  const { t } = useTranslation();
  const maxValue = Math.max(...data.map((item) => item.value), 1);

  return (
    <div className="device-analytics-card device-bar-list-card">
      <div className="device-card-title-row">
        <h3>{t(title)}</h3>
        {showViewAll && <button type="button">{t("VIEW ALL")}</button>}
      </div>
      <div className="device-bar-list">
        {data.map((item, index) => (
          <div className="device-bar-row" key={`${item.name}-${index}`}>
            <div className="device-bar-row-label">
              <span>{t(item.name, { defaultValue: item.name })}</span>
              <strong>{item.value}</strong>
            </div>
            <div className="device-bar-track">
              <div
                className={index === data.length - 1 ? "warning" : ""}
                style={{ width: `${Math.max((item.value / maxValue) * 100, 8)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthTrendCard({ data }) {
  const { t } = useTranslation();
  const hasData = Array.isArray(data) && data.length > 0;

  return (
    <div className="device-analytics-card device-health-card">
      <div className="device-card-title-row">
        <h3>{t("Devices Health Trend")}</h3>
      </div>
      <div className="device-health-legend">
        <span><i className="active" />{t("Active")}</span>
        <span><i className="inactive" />{t("In Active")}</span>
      </div>
      {hasData ? (
        <ResponsiveContainer width="100%" height={155}>
          <LineChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: -18 }}>
            <CartesianGrid stroke="#E8EDF5" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
            <Tooltip />
            <Line type="monotone" dataKey="active" stroke="#22C55E" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="inactive" stroke="#EF4444" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="device-health-empty">{t("No health trend data available")}</div>
      )}
    </div>
  );
}

function CriticalAlertsCard({ summary, alerts }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="device-analytics-card device-alerts-card">
      <div className="device-card-title-row">
        <h3>{t("Critical Alerts (Live)")}</h3>
        <button
          type="button"
          onClick={() =>
            navigate("/device/monitor", {
              state: { breadcrumbSource: "devices" },
            })
          }
        >
          {t("View All")}
        </button>
      </div>
      <div className="device-alert-summary-grid">
        {summary.map((item) => {
          const Icon = item.icon;
          return (
            <div className="device-alert-summary-item" key={item.label}>
              <span><Icon sx={{ fontSize: 16 }} /></span>
              <div>
                <small>{t(item.label, { defaultValue: item.label })}</small>
                <strong>{item.count}</strong>
              </div>
            </div>
          );
        })}
      </div>
      <div className="device-alert-list">
        {alerts.length === 0 ? (
          <div className="device-alert-empty">{t("No alerts")}</div>
        ) : alerts.map((alert) => {
          const Icon = alert.icon || DirectionsBusIcon;
          return (
            <div className="device-alert-row" key={alert.id}>
              <span className="device-alert-row-icon"><Icon sx={{ fontSize: 16 }} /></span>
              <div>
                <strong>{alert.deviceName}</strong>
                <small>{alert.message}</small>
              </div>
              {(alert.value || alert.exceededBy) && (
                <em>
                  {alert.value && <strong>{alert.value}</strong>}
                  {alert.exceededBy && <small>{alert.exceededBy}</small>}
                </em>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DeviceList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { organizationInactive } = useUser();
  const [devices, setDevices] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [expandedDeviceId, setExpandedDeviceId] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [showCreateDevice, setShowCreateDevice] = useState(false);
  const [deviceAlerts, setDeviceAlerts] = useState([]);
  const [visibleDeviceCount, setVisibleDeviceCount] = useState(
    DEVICE_TABLE_BATCH_SIZE,
  );
  const [healthTrendData, setHealthTrendData] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(DEFAULT_DATE_RANGE);
  const [sortBy, setSortBy] = useState("Newest First");
  const alertRefreshTimeoutRef = useRef(null);
  const safeDevices = useMemo(() => (Array.isArray(devices) ? devices : []), [devices]);

  const getCurrentUserState = () => {
    try {
      return JSON.parse(localStorage.getItem("userState") || "{}");
    } catch (error) {
      console.warn("Error parsing localStorage:", error);
      return {};
    }
  };

  const userState = getCurrentUserState();
  const currentUser = userState?.user || {};
  const currentOrganizationId =
    currentUser?.organizationId || userState?.organization?.id;
  const roleName = String(
    currentUser?.role?.name ||
      currentUser?.role ||
      userState?.role?.name ||
      userState?.role ||
      "",
  ).toLowerCase();
  const roleId =
    currentUser?.roleId ||
    userState?.roleId ||
    currentUser?.role?.id ||
    userState?.role?.id;
  const isSuperAdmin =
    currentUser?.email === "sales@routeeye.io" ||
    Number(roleId) === 3 ||
    roleName.includes("superadmin") ||
    roleName.includes("super admin");
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const selectedApp = localStorage.getItem("selectedApp") || "vehicleTrack";
  const devicePageId =
    selectedApp === "schoolTrack" ? PAGE_IDS.SCHOOL_DEVICES : PAGE_IDS.FLEET_DEVICES;
  const { layout: moduleLayout } = usePageLayout(
    useJsonLayout ? devicePageId : null,
  );
  const roleKey = isSuperAdmin ? "SUPER_ADMIN" : "ORG_ADMIN";
  const pageTitleSection = getLayoutSection(moduleLayout, "pageTitle", "list");
  const actionSection = getLayoutSection(moduleLayout, "actionBar", "list");
  const summarySection = getLayoutSection(moduleLayout, "summaryStats", "list");
  const analyticsSection = getLayoutSection(moduleLayout, "analyticsGrid", "list");
  const filterSection = getLayoutSection(moduleLayout, "filterBar", "list");
  const tableSection = getLayoutSection(moduleLayout, "dataTable", "list");
  const actionBarWidgets = useMemo(
    () => {
      const layoutActions = getVisibleLayoutWidgets(actionSection, roleKey);
      if (useJsonLayout) return layoutActions;

      return [
        {
          actionKey: "monitorDevices",
          labelKey: "Live Device Monitoring",
          path: "/device/monitor",
        },
        {
          actionKey: "createDevice",
          labelKey: "Create Device",
        },
      ];
    },
    [actionSection, roleKey, useJsonLayout],
  );
  const createAction = actionBarWidgets.find(
    (widget) => widget.actionKey === "createDevice",
  );
  const monitorAction = actionBarWidgets.find(
    (widget) => widget.actionKey === "monitorDevices",
  );
  const canCreateDevice = isSuperAdmin && Boolean(createAction);
  const canUseCreateAction = canCreateDevice;
  const canUseMonitorAction = Boolean(monitorAction);
  const tableFeatures = tableSection?.features || {};
  const tableBatchSize = Number(tableFeatures.batchSize) || DEVICE_TABLE_BATCH_SIZE;
  const isShowMoreEnabled = tableFeatures.showMore !== false;
  const isExpandableRowsEnabled = tableFeatures.expandableRows !== false;

  const fetchDevices = useCallback(async () => {
    try {
      console.log("Fetching devices...");
      if (!isSuperAdmin && !currentOrganizationId) {
        setDevices([]);
        return;
      }

      const response = isSuperAdmin
        ? await getAllTrackingDevices()
        : await getTrackingDevicesByOrgIdIndependent(currentOrganizationId);
      console.log("Raw API response:", response);

      const deviceData = response?.data || [];
      console.log("Device data:", deviceData);
      const rawDevices = Array.isArray(deviceData) ? deviceData : [];
      const organizationIds = Array.from(
        new Set(
          rawDevices
            .map(getDeviceOrganizationId)
            .concat(currentOrganizationId || [])
            .filter(Boolean)
            .map(String),
        ),
      );
      const sites = (
        await Promise.all(
          organizationIds.map(async (organizationId) => {
            try {
              const siteResponse = await axios.get(
                `${config.baseURL}/site/organization?organizationId=${organizationId}`,
                { withCredentials: true },
              );
              return Array.isArray(siteResponse.data)
                ? siteResponse.data
                : siteResponse.data?.sites || siteResponse.data?.data || [];
            } catch (error) {
              console.warn(`Unable to fetch sites for organization ${organizationId}:`, error);
              return [];
            }
          }),
        )
      ).flat();
      const siteLookup = buildSiteLookup(sites);
      const latestReadings = organizationInactive
        ? []
        : (
            await Promise.allSettled(
              organizationIds.map((organizationId) =>
                getDeviceMonitorLatest({ organizationId }),
              ),
            )
          ).flatMap((result) => {
            if (result.status !== "fulfilled") return [];
            const responseData = result.value?.data || result.value || [];
            return Array.isArray(responseData) ? responseData : [];
          });
      const latestReadingLookup = buildLatestReadingLookup(latestReadings);

      const safeDeviceData = rawDevices.map((device) => {
        const latestReading =
          latestReadingLookup.get(String(device.id || "")) ||
          latestReadingLookup.get(String(device.trackingDeviceId || "")) ||
          latestReadingLookup.get(String(device.deviceId || ""));
        return normalizeDeviceForList(
          mergeDeviceWithLatestReading(device, latestReading),
          siteLookup,
        );
      });
      setDevices(safeDeviceData);
    } catch (error) {
      console.error("Error fetching devices:", error);
      console.error("Error details:", error.message);
      console.error("Error stack:", error.stack);
      setDevices([]);
    }
  }, [currentOrganizationId, isSuperAdmin, organizationInactive]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const fetchDeviceAlerts = useCallback(async () => {
    if (organizationInactive) {
      setDeviceAlerts([]);
      return;
    }
    const organizationIds = isSuperAdmin
      ? getUniqueDeviceOrganizationIds(safeDevices)
      : currentOrganizationId
        ? [String(currentOrganizationId)]
        : [];

    if (!organizationIds.length) {
      setDeviceAlerts([]);
      return;
    }

    try {
      const rangeParams = getDateRangeParams(selectedDateRange);
      const results = await Promise.allSettled(
        organizationIds.map((organizationId) =>
          getDeviceMonitorAlerts({
            organizationId,
            status: "all",
            ...rangeParams,
            limit: 500,
          }),
        ),
      );

      const mergedAlerts = new Map();
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const response = result.value;
        const alertData = Array.isArray(response?.data)
          ? response.data
          : response?.data?.alerts || response?.alerts || response || [];

        if (!Array.isArray(alertData)) return;
        alertData.forEach((alert) => {
          mergedAlerts.set(getDeviceAlertRecordKey(alert), alert);
        });
      });

      setDeviceAlerts(Array.from(mergedAlerts.values()));
    } catch (error) {
      console.warn("Unable to fetch device alerts:", error);
      setDeviceAlerts([]);
    }
  }, [currentOrganizationId, isSuperAdmin, organizationInactive, safeDevices, selectedDateRange]);

  useEffect(() => {
    fetchDeviceAlerts();
  }, [fetchDeviceAlerts]);

  useEffect(() => {
    if (organizationInactive || !currentOrganizationId) return undefined;

    let isCancelled = false;
    let abortController = null;

    const rangeParams = getDateRangeParams(selectedDateRange);

    const upsertStreamAlert = (alertRecord) => {
      if (!alertRecord) return;
      const alertDate = alertRecord.createdAt || alertRecord.recordedAt || alertRecord.updatedAt;
      if (!isDateWithinRange(alertDate, rangeParams)) return;

      const nextKey = getDeviceAlertRecordKey(alertRecord);
      setDeviceAlerts((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const existingIndex = list.findIndex(
          (item) => getDeviceAlertRecordKey(item) === nextKey,
        );

        if (existingIndex === -1) return [alertRecord, ...list].slice(0, 500);

        const copy = [...list];
        copy[existingIndex] = { ...copy[existingIndex], ...alertRecord };
        return copy;
      });

      if (alertRefreshTimeoutRef.current) return;
      alertRefreshTimeoutRef.current = setTimeout(() => {
        alertRefreshTimeoutRef.current = null;
        fetchDeviceAlerts();
      }, 500);
    };

    const handleStreamPayload = (payload) => {
      if (!payload || payload.type === "connected" || payload.type === "heartbeat") return;
      setDevices((currentDevices) =>
        (Array.isArray(currentDevices) ? currentDevices : []).map((device) => {
          const isSameDevice = getLatestReadingKeyCandidates(payload).some((key) =>
            [
              device.id,
              device.trackingDeviceId,
              device.deviceId,
            ]
              .filter((value) => value !== null && value !== undefined && value !== "")
              .map(String)
              .includes(key),
          );
          return isSameDevice ? mergeDeviceWithLatestReading(device, payload) : device;
        }),
      );
      const alertRecord = buildDeviceListStreamAlert(payload);
      upsertStreamAlert(alertRecord);
    };

    const connectStream = async () => {
      abortController = new AbortController();
      const accessToken = getStoredAccessToken();

      try {
        const response = await fetch(getDeviceStreamUrl({ organizationId: currentOrganizationId }), {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          credentials: "include",
          signal: abortController.signal,
        });

        if (!response.ok || !response.body?.getReader) return;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!isCancelled) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const blocks = buffer.split(/\r?\n\r?\n/);
          buffer = blocks.pop() || "";

          blocks.forEach((block) => {
            try {
              handleStreamPayload(parseSseBlock(block));
            } catch (error) {
              console.warn("Failed to parse device alert stream event:", error);
            }
          });
        }
      } catch (error) {
        if (!isCancelled && error.name !== "AbortError") {
          console.warn("Device alert stream connection failed:", error);
        }
      }
    };

    connectStream();

    return () => {
      isCancelled = true;
      abortController?.abort();
      if (alertRefreshTimeoutRef.current) {
        clearTimeout(alertRefreshTimeoutRef.current);
        alertRefreshTimeoutRef.current = null;
      }
    };
  }, [currentOrganizationId, fetchDeviceAlerts, organizationInactive, selectedDateRange]);

  const legacyHeaders = useMemo(
    () => [
      { key: "deviceId", label: "Device ID", width: "150px" },
      {
        key: "deviceType",
        label: "Device Type",
        width: "170px",
        render: (row) => t(row.deviceType, { defaultValue: row.deviceType }),
      },
      ...(isSuperAdmin
        ? [{ key: "organization", label: "Organization", width: "170px" }]
        : []),
      {
        key: "status",
        label: "Status",
        width: "110px",
        render: (row) => (
          <TableStatusBadge tone={getStatusBadgeTone(row.status)}>
            {t(row.status)}
          </TableStatusBadge>
        ),
      },
      {
        key: "attachedTo",
        label: "Attached To",
        width: "150px",
        render: (row) => t(row.attachedTo, { defaultValue: row.attachedTo }),
      },
    ],
    [isSuperAdmin, t],
  );

  const headers = useMemo(() => {
    const layoutColumns = getVisibleLayoutColumns(tableSection, roleKey);
    if (!useJsonLayout && !layoutColumns.length) return legacyHeaders;

    return layoutColumns
      .filter(
        (column) =>
          isSuperAdmin || (column.fieldKey || column.headerKey || column.id) !== "organization",
      )
      .map((column) => {
        const key = column.fieldKey || column.headerKey || column.id;
        return {
          key,
          label: column.labelKey || column.headerKey || column.id,
          width: column.width,
          render:
            key === "status"
              ? (row) => (
                  <TableStatusBadge tone={getStatusBadgeTone(row.status)}>
                    {t(row.status)}
                  </TableStatusBadge>
                )
              : key === "deviceType" || key === "attachedTo"
                ? (row) => t(row[key], { defaultValue: row[key] })
              : undefined,
        };
      });
  }, [isSuperAdmin, legacyHeaders, roleKey, tableSection, t, useJsonLayout]);

  const formatWorkingCondition = (condition) => {
    switch (condition) {
      case "WORKING":
        return "Yes";
      case "NOT_WORKING":
        return "No";
      case "NEEDS_REPAIR":
        return "No";
      default:
        return "N/A";
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case "INSTALLED":
        return "Yes";
      case "PENDING":
        return "No";
      default:
        return "-";
    }
  };

  useEffect(() => {
    if (organizationInactive) {
      setHealthTrendData([]);
      return undefined;
    }

    let isMounted = true;

    const fetchHealthTrend = async () => {
      const trendBuckets = getTrendDays(selectedDateRange);
      const supportedDevices = safeDevices.filter(
        (device) => device?.id && getHistoryKey(device),
      );

      if (!supportedDevices.length || !trendBuckets.length) {
        setHealthTrendData([]);
        return;
      }

      const from = trendBuckets[0].date.toISOString();
      const toDate = trendBuckets[trendBuckets.length - 1].endDate;
      const bucketType = trendBuckets[0].bucketType;

      const results = await Promise.allSettled(
        supportedDevices.map(async (device) => {
          const historyKey = getHistoryKey(device);
          const response = await getDeviceHistory(device.id, {
            sensorType: historyKey,
            from,
            to: toDate.toISOString(),
            limit: 500,
          });

          const rows = Array.isArray(response?.data) ? response.data : [];
          const latestRowByBucket = new Map();

          rows.forEach((row) => {
            const bucketKey = getTrendBucketKey(row?.recordedAt, bucketType);
            if (!bucketKey) return;

            const existingRow = latestRowByBucket.get(bucketKey);
            const existingTime = existingRow?.recordedAt
              ? new Date(existingRow.recordedAt).getTime()
              : 0;
            const rowTime = row?.recordedAt ? new Date(row.recordedAt).getTime() : 0;

            if (!existingRow || rowTime >= existingTime) {
              latestRowByBucket.set(bucketKey, row);
            }
          });

          return {
            device,
            latestRowByBucket,
          };
        }),
      );

      if (!isMounted) return;

      const deviceRows = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);

      if (!deviceRows.length) {
        setHealthTrendData([]);
        return;
      }

      setHealthTrendData(
        trendBuckets.map((bucket) => {
          const active = deviceRows.filter(({ latestRowByBucket }) =>
            latestRowByBucket.has(bucket.key),
          ).length;

          return {
            day: bucket.day,
            active,
            inactive: deviceRows.length - active,
          };
        }),
      );
    };

    fetchHealthTrend().catch((error) => {
      console.warn("Unable to fetch device health trend:", error);
      if (isMounted) setHealthTrendData([]);
    });

    return () => {
      isMounted = false;
    };
  }, [organizationInactive, safeDevices, selectedDateRange]);

  const filteredDevices = useMemo(() => {
    const result = safeDevices.filter((device) => {
      // Filter logic similar to Vehicle component
      const deviceTypeMatch =
        !filters.deviceType || device.deviceType === filters.deviceType;
      const statusMatch =
        !filters.status || getUiStatus(device) === filters.status;

      const organizationMatch =
        !isSuperAdmin ||
        !filters.organization ||
        String(getInstalledOrganizationName(device)) === String(filters.organization);

      const attachedToMatch =
        !filters.attachedTo || getAttachedTo(device) === filters.attachedTo;

      // If any filter doesn't match, exclude the device
      if (!deviceTypeMatch || !statusMatch || !organizationMatch || !attachedToMatch) {
        return false;
      }

      // If no search query, return true (show all that match filters)
      if (!searchTerm) {
        return true;
      }

      // Otherwise, search in all relevant fields
      const query = searchTerm.toLowerCase();
      return (
        (device.deviceId && device.deviceId.toLowerCase().includes(query)) ||
        // (device.imeiNumber &&
        //   device.imeiNumber.toLowerCase().includes(query)) ||
        formatWorkingCondition(device.workingCondition)
          .toLowerCase()
          .includes(query) ||
        getUiStatus(device).toLowerCase().includes(query) ||
        formatStatus(device.status).toLowerCase().includes(query) ||
        (device.simCard?.simNumber &&
          device.simCard.simNumber.toLowerCase().includes(query)) ||
        (device.organization?.name &&
          device.organization.name.toLowerCase().includes(query)) ||
        (device.organizationName &&
          String(device.organizationName).toLowerCase().includes(query)) ||
        (device.stop?.name &&
          device.stop.name.toLowerCase().includes(query)) ||
        (device.stopName &&
          String(device.stopName).toLowerCase().includes(query)) ||
        (device.vehicle?.name &&
          device.vehicle.name.toLowerCase().includes(query)) ||
        (device.vehicleName &&
          String(device.vehicleName).toLowerCase().includes(query))
      );
    });

    return [...result].sort((a, b) => {
      const newestFirst = sortBy === "Newest First";
      const timeA = new Date(
        a.createdAt || a.created_at || a.createdDate || a.created_date || 0,
      ).getTime();
      const timeB = new Date(
        b.createdAt || b.created_at || b.createdDate || b.created_date || 0,
      ).getTime();

      if (timeA !== timeB) {
        return newestFirst ? timeB - timeA : timeA - timeB;
      }

      const idA = Number(a.id || 0);
      const idB = Number(b.id || 0);
      return newestFirst ? idB - idA : idA - idB;
    });
  }, [safeDevices, filters, searchTerm, isSuperAdmin, sortBy]);

  // Transform filtered devices to table format
  const filteredTableData = useMemo(() => {
    return filteredDevices.map((device) => {
      const simCard = device?.simCard || {};
      return {
        deviceId: device?.deviceId || "-",
        imeiNumber: device?.imeiNumber || "-",
        workingCondition: formatWorkingCondition(device?.workingCondition),
        status: getUiStatus(device),
        simCardId: simCard?.simNumber || "-",
        "simCard.expiryDate": simCard?.planExpiry
          ? new Date(simCard.planExpiry).toLocaleDateString("en-GB")
          : "-",
        organization: getInstalledOrganizationName(device),
        attachedTo: getAttachedTo(device),
        deviceType: (() => {
          if (!device?.deviceType) return "-";
          const formattedType = formatDeviceTypeLabel(device.deviceType);

          if (
            device.deviceType.toUpperCase() === "SENSOR" &&
            device?.sensorType
          ) {
            const formattedSensorType = formatDeviceTypeLabel(
              device.sensorType,
            );
            return `${formattedType} - ${formattedSensorType}`;
          }

          return formattedType;
        })(),
        id: device?.id,
        originalData: device,
      };
    });
  }, [filteredDevices]);

  useEffect(() => {
    setVisibleDeviceCount(tableBatchSize);
  }, [
    filters.attachedTo,
    filters.deviceType,
    filters.organization,
    filters.status,
    searchTerm,
    sortBy,
    tableBatchSize,
  ]);

  const visibleTableData = useMemo(() => {
    if (!isShowMoreEnabled) return filteredTableData;
    return filteredTableData.slice(0, visibleDeviceCount);
  }, [filteredTableData, isShowMoreEnabled, visibleDeviceCount]);

  const hasMoreDevices =
    isShowMoreEnabled && visibleDeviceCount < filteredTableData.length;

  const handleShowMoreDevices = () => {
    setVisibleDeviceCount((currentCount) =>
      Math.min(currentCount + tableBatchSize, filteredTableData.length),
    );
  };

  const tableFilters = useMemo(() => {
    const uniqueOptions = (values) =>
      Array.from(new Set(values.filter(Boolean))).map((value) => ({
        value,
        label: value,
      }));

    const baseFilters = [
      {
        key: "deviceType",
        label: "Device Type",
        value: filters.deviceType || "",
        onChange: (value) =>
          setFilters((current) => ({ ...current, deviceType: value })),
        options: [
          { value: "", label: "Device Type" },
          ...Array.from(
            new Map(
              safeDevices
                .filter((device) => device.deviceType)
                .map((device) => [
                  device.deviceType,
                  {
                    value: device.deviceType,
                    label: formatDeviceTypeLabel(device.deviceType),
                  },
                ]),
            ).values(),
          ),
        ],
      },
      {
        key: "status",
        label: "Status",
        value: filters.status || "",
        onChange: (value) =>
          setFilters((current) => ({ ...current, status: value })),
        options: [
          { value: "", label: "All Status" },
          { value: "Active", label: "Active" },
          { value: "Warning", label: "Warning" },
          { value: "Inactive", label: "Inactive" },
        ],
      },
      {
        key: "attachedTo",
        label: "Attached To",
        value: filters.attachedTo || "",
        onChange: (value) =>
          setFilters((current) => ({ ...current, attachedTo: value })),
        options: [
          { value: "", label: "Attached To" },
          ...uniqueOptions(safeDevices.map(getAttachedTo).filter((value) => value !== "-")),
        ],
      },
      {
        key: "sortBy",
        label: "Sort By",
        value: sortBy,
        onChange: (val) => setSortBy(val),
        options: [
          { value: "Newest First", label: "Newest First" },
          { value: "Oldest First", label: "Oldest First" },
        ],
      },
    ];

    const allFilters = [
      ...baseFilters.slice(0, 2),
      {
        key: "organization",
        label: "Organization",
        value: filters.organization || "",
        onChange: (value) =>
          setFilters((current) => ({ ...current, organization: value })),
        options: [
          { value: "", label: "Organization" },
          ...uniqueOptions(safeDevices.map(getInstalledOrganizationName)),
        ],
      },
      ...baseFilters.slice(2),
    ];

    const defaultFilters = isSuperAdmin ? allFilters : baseFilters;
    const layoutWidgets = getVisibleLayoutWidgets(filterSection, roleKey).filter(
      (widget) => widget.type === "tableFilter",
    );

    let activeFilters = defaultFilters;
    if (useJsonLayout && layoutWidgets.length) {
      const filterByKey = new Map(allFilters.map((filter) => [filter.key, filter]));
      activeFilters = layoutWidgets
        .filter((widget) => isSuperAdmin || widget.filterKey !== "organization")
        .map((widget) => filterByKey.get(widget.filterKey))
        .filter(Boolean);
    }

    if (!activeFilters.some((filter) => filter.key === "sortBy")) {
      const sortFilter = allFilters.find((f) => f.key === "sortBy") || {
        key: "sortBy",
        label: "Sort By",
        value: sortBy,
        onChange: (val) => setSortBy(val),
        options: [
          { value: "Newest First", label: "Newest First" },
          { value: "Oldest First", label: "Oldest First" },
        ],
      };
      activeFilters = [...activeFilters, sortFilter];
    }

    return activeFilters;
  }, [filters, safeDevices, isSuperAdmin, filterSection, roleKey, useJsonLayout, sortBy]);

  const deviceAnalytics = useMemo(() => {
    const total = safeDevices.length;

    const typeDistribution = buildDistribution(
      safeDevices,
      getDeviceDisplayType,
      safeDevices.length,
    );
    const getCity = (device) =>
      device?.organization?.city || device?.city || "Unassigned";
    const getSite = (device) =>
      device?.site?.name ||
      device?.siteName ||
      device?.assignedVehicle?.site?.name ||
      device?.assignedVehicle?.siteId ||
      device?.stop?.site?.name ||
      device?.assignedStop?.site?.name ||
      device?.assignedStop?.siteId ||
      device?.siteId ||
      "Unassigned";
    const getOrganizationName = (device) =>
      device?.organization?.name || device?.organizationName || "Unassigned";
    const ownershipDistribution = buildDistribution(
      safeDevices,
      (device) => device?.ownedBy || "Unassigned",
      5,
    );

    const cityDistribution = buildDistribution(safeDevices, getCity, safeDevices.length);
    const siteDistribution = buildDistribution(safeDevices, getSite, safeDevices.length);
    const organizationBarDistribution = buildDistribution(
      safeDevices,
      getOrganizationName,
      safeDevices.length,
    );
    const topList = (distribution) =>
      distribution
        .map((item) => ({ name: item.name, value: item.value }))
        .slice(0, 5);

    return {
      total,
      typeDistribution,
      ownershipDistribution,
      cityData: topList(cityDistribution),
      siteData: topList(siteDistribution),
      organizationData: topList(organizationBarDistribution),
      cityCount: cityDistribution.length,
      siteCount: siteDistribution.length,
      organizationCount: organizationBarDistribution.length,
    };
  }, [safeDevices]);

  const alertAnalytics = useMemo(() => {
    const normalizedAlerts = deviceAlerts.map((alert, index) => {
      const matchedType = getAlertDeviceTypeDefinition(alert);
      const trackingDevice = getAlertTrackingDevice(alert);
      const matchedDevice = getAlertDeviceMatch(alert, safeDevices);
      const thresholdCopy = getAlertThresholdCopy(alert, matchedDevice);
      const deviceName =
        alert?.deviceId ||
        trackingDevice?.deviceId ||
        alert?.vehicleName ||
        alert?.vehicle?.name ||
        `DEVICE-${index + 1}`;

      return {
        id: alert?.id || `${deviceName}-${index}`,
        type: matchedType.key,
        label: matchedType.label,
        icon: matchedType.icon,
        deviceName,
        message: titleCase(alert?.message || alert?.description || `${matchedType.label} Alert`),
        value: thresholdCopy.valueText || alert?.value || alert?.reading || alert?.status || "",
        exceededBy: thresholdCopy.detail,
      };
    });

    const countByType = normalizedAlerts.reduce((counts, alert) => {
      counts.set(alert.type, (counts.get(alert.type) || 0) + 1);
      return counts;
    }, new Map());
    const organizationAlertTypes = safeDevices
      .map(getDeviceTypeDefinition)
      .filter(Boolean)
      .reduce((types, type) => {
        if (!types.some((item) => item.key === type.key)) types.push(type);
        return types;
      }, []);
    normalizedAlerts.forEach((alert) => {
      if (!organizationAlertTypes.some((type) => type.key === alert.type)) {
        organizationAlertTypes.push({
          key: alert.type,
          label: alert.label,
          icon: alert.icon,
        });
      }
    });

    const summary = organizationAlertTypes.map((type) => ({
      ...type,
      count: countByType.get(type.key) || 0,
    }));

    return { summary, alerts: normalizedAlerts };
  }, [deviceAlerts, safeDevices]);

  const analyticsWidgets = useMemo(() => {
    const widgets = getVisibleLayoutWidgets(analyticsSection, roleKey).filter(
      (widget) => widget.type === "analyticsCard",
    );

    if (useJsonLayout) return widgets;

    return [
      { id: "typeDistribution", widgetKey: "devicesByType", labelKey: "Devices by Type" },
      {
        id: "ownershipDistribution",
        widgetKey: "devicesByOwnership",
        labelKey: "Devices by Ownership",
      },
      { id: "criticalAlerts", widgetKey: "criticalAlerts", labelKey: "Critical Alerts (Live)" },
      { id: "healthTrend", widgetKey: "healthTrend", labelKey: "Devices Health Trend" },
      { id: "city", widgetKey: "devicesByCity", labelKey: "Devices by City", group: "bars" },
      { id: "site", widgetKey: "devicesBySite", labelKey: "Devices by Site", group: "bars" },
      {
        id: "organization",
        widgetKey: "devicesByOrganization",
        labelKey: "Devices by Organization",
        group: "bars",
      },
    ];
  }, [analyticsSection, roleKey, useJsonLayout]);

  const renderAnalyticsWidget = (widget) => {
    switch (widget.widgetKey) {
      case "devicesByType":
        return (
          <DonutAnalyticsCard
            key={widget.id}
            title={widget.labelKey || "Devices by Type"}
            data={deviceAnalytics.typeDistribution}
            total={deviceAnalytics.total}
            colors={CHART_COLORS}
          />
        );
      case "devicesByOwnership":
        return (
          <DonutAnalyticsCard
            key={widget.id}
            title={widget.labelKey || "Devices by Ownership"}
            data={deviceAnalytics.ownershipDistribution}
            total={deviceAnalytics.total}
            colors={OWNER_COLORS}
          />
        );
      case "criticalAlerts":
        return (
          <CriticalAlertsCard
            key={widget.id}
            summary={alertAnalytics.summary}
            alerts={alertAnalytics.alerts}
          />
        );
      case "healthTrend":
        return (
          <HealthTrendCard
            key={widget.id}
            data={healthTrendData}
          />
        );
      case "devicesByCity":
        return (
          <DeviceBarListCard
            key={widget.id}
            title={widget.labelKey || "Devices by City"}
            data={deviceAnalytics.cityData}
            showViewAll={deviceAnalytics.cityCount > deviceAnalytics.cityData.length}
          />
        );
      case "devicesBySite":
        return (
          <DeviceBarListCard
            key={widget.id}
            title={widget.labelKey || "Devices by Site"}
            data={deviceAnalytics.siteData}
            showViewAll={deviceAnalytics.siteCount > deviceAnalytics.siteData.length}
          />
        );
      case "devicesByOrganization":
        return (
          <DeviceBarListCard
            key={widget.id}
            title={widget.labelKey || "Devices by Organization"}
            data={deviceAnalytics.organizationData}
            showViewAll={
              deviceAnalytics.organizationCount > deviceAnalytics.organizationData.length
            }
          />
        );
      default:
        return null;
    }
  };

  const statsCardData = useMemo(() => {
    const totalDevices = safeDevices.length;
    const installedDevices = safeDevices.filter(
      (device) => device?.status === "INSTALLED",
    ).length;
    const activeDevices = safeDevices.filter(
      (device) => getUiStatus(device) === "Active",
    ).length;
    const devicesWithIssues = safeDevices.filter((device) =>
      ["NOT_WORKING", "NEEDS_REPAIR"].includes(device?.workingCondition),
    ).length;
    const simExpiryNext30Days = safeDevices.filter((device) => {
      try {
        if (!device?.simCard?.planExpiry) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expiryDate = new Date(device.simCard.planExpiry);
        expiryDate.setHours(0, 0, 0, 0);

        const diffDays = (expiryDate - today) / (1000 * 60 * 60 * 24);
        return diffDays >= 0 && diffDays <= 30;
      } catch (error) {
        return false;
      }
    }).length;
    const avgUptime = totalDevices
      ? Math.round((activeDevices / totalDevices) * 100)
      : 0;
    const metricValues = {
      totalDevices,
      installedDevices,
      activeDevices,
      devicesWithIssues,
      simExpiryNext30Days,
      avgUptime: `${avgUptime}%`,
    };
    const legacyCards = [
      {
        title: "Total Devices",
        metricKey: "totalDevices",
        icon: TotalDevicesStatsIcon,
        tone: "blue",
      },
      {
        title: "Installed Devices",
        metricKey: "installedDevices",
        icon: InstalledDevicesStatsIcon,
        tone: "amber",
      },
      {
        title: "Active Devices",
        metricKey: "activeDevices",
        icon: ActiveDevicesStatsIcon,
        tone: "green",
      },
      {
        title: "Devices With Issues",
        metricKey: "devicesWithIssues",
        icon: DeviceIssuesStatsIcon,
        tone: "red",
      },
      {
        title: "SIM Expiry in Next 30 Days",
        metricKey: "simExpiryNext30Days",
        icon: SimExpiryStatsIcon,
        tone: "red",
      },
      {
        title: "Avg. Uptime",
        metricKey: "avgUptime",
        icon: AvgUptimeStatsIcon,
        tone: "blue",
      },
    ];

    const layoutWidgets = getVisibleLayoutWidgets(summarySection, roleKey).filter(
      (widget) => widget.type === "statCard",
    );
    const cardConfig = useJsonLayout
      ? layoutWidgets.map((widget) => ({
          title: widget.labelKey,
          metricKey: widget.metricKey,
          icon: DEVICE_STAT_ICON_BY_METRIC[widget.metricKey] || TotalDevicesStatsIcon,
          tone: widget.variant || "blue",
        }))
      : layoutWidgets.length
        ? layoutWidgets.map((widget) => ({
            title: widget.labelKey,
            metricKey: widget.metricKey,
            icon: DEVICE_STAT_ICON_BY_METRIC[widget.metricKey] || TotalDevicesStatsIcon,
            tone: widget.variant || "blue",
          }))
        : legacyCards;

    return cardConfig.map((card) => ({
      title: card.title,
      value: metricValues[card.metricKey] ?? 0,
      icon: card.icon,
      tone: card.tone,
    }));
  }, [roleKey, safeDevices, summarySection, useJsonLayout]);

  // Handle search input change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const toggleExpandedRow = (deviceId) => {
    setExpandedDeviceId((currentId) =>
      String(currentId || "") === String(deviceId || "") ? null : deviceId,
    );
  };

  const handleInlineDeleteClick = (rowData) => {
    setDeviceToDelete({
      id: rowData.id,
      name: rowData.originalData?.deviceId || "Unknown device",
      data: rowData.originalData,
    });
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deviceToDelete) {
      console.error("No device to delete");
      return;
    }

    console.log("Attempting to delete device:", deviceToDelete);
    setIsDeleting(true);

    try {
      const deleteResponse = await deleteTrackingDevice(deviceToDelete.id);
      console.log("Delete response:", deleteResponse);

      // Update the devices state by removing the deleted device
      setDevices((prevDevices) => {
        const filteredDevices = (
          Array.isArray(prevDevices) ? prevDevices : []
        ).filter((device) => device?.id !== deviceToDelete.id);
        console.log("Updated devices after delete:", filteredDevices);
        return filteredDevices;
      });

      setShowDeleteModal(false);
      setExpandedDeviceId((currentId) =>
        String(currentId || "") === String(deviceToDelete.id) ? null : currentId,
      );
      setDeviceToDelete(null);
      console.log(`Device ${deviceToDelete.name} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete device:", error);
      console.error("Delete error details:", error.message);
      console.error("Delete error response:", error.response?.data);
      alert(`Failed to delete device: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeviceToDelete(null);
  };

  // Safe localStorage access
  const getOrgName = () => {
    return userState?.organization?.name || "Organization";
  };

  const orgName = getOrgName();
  const pageTitle = pageTitleSection?.props?.labelKey || "Devices";
  const primaryAnalyticsWidgets = analyticsWidgets.filter((widget) => widget.group !== "bars");
  const barAnalyticsWidgets = analyticsWidgets.filter((widget) => widget.group === "bars");
  const shouldRenderTable = !useJsonLayout || Boolean(tableSection);

  return (
    <>
      <SubTitleNew
        title={pageTitle}
        path={`${orgName} > ${pageTitle}`}
        add={
          !organizationInactive && canUseCreateAction
            ? createAction?.labelKey || "Create Device"
            : undefined
        }
        onAddClick={
          !organizationInactive && canUseCreateAction
            ? () => setShowCreateDevice((prev) => !prev)
            : undefined
        }
        track={
          !organizationInactive && canUseMonitorAction
            ? monitorAction?.labelKey
            : undefined
        }
        onTrackClick={
          !organizationInactive && canUseMonitorAction
            ? () => {
                navigate(monitorAction.path || "/device/monitor", {
                  state: { breadcrumbSource: "devices" },
                });
              }
            : undefined
        }
        dateOptions={organizationInactive ? undefined : DEVICE_DATE_OPTIONS}
        onDateRangeChange={organizationInactive ? undefined : setSelectedDateRange}
        showDashboardControls={!organizationInactive}
      />
      <div className="device-new-page-body">
        {organizationInactive ? (
          <div className="table_container device-table-container">
            <InactiveInventoryTable
              headers={legacyHeaders.filter((header) => header.key !== "status")}
              data={filteredTableData}
              loading={false}
              emptyMessage={t("No devices found")}
              searchPlaceholder={t("Search devices")}
              itemLabel="devices"
              minWidth={760}
            />
          </div>
        ) : (
          <>
            {canUseCreateAction && showCreateDevice && (
              <Box
                sx={{
                  mx: { xs: 2.5, md: 5 },
                  mb: 3,
                  border: "1px solid #A5DCF3",
                  borderRadius: "10px",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "-2px 1px 5px 0px #00000040",
                }}
              >
                <CreateDevice
                  embedded
                  onCancel={() => setShowCreateDevice(false)}
                  onComplete={() => {
                    setShowCreateDevice(false);
                    fetchDevices();
                  }}
                />
              </Box>
            )}
            {statsCardData.length > 0 && (
              <div className="stats-card-grid device-stats-card-grid">
                {statsCardData.map((card) => (
                  <StatsCard key={card.title} {...card} />
                ))}
              </div>
            )}
            {primaryAnalyticsWidgets.length > 0 && (
              <div className="device-analytics-grid">
                {primaryAnalyticsWidgets.map(renderAnalyticsWidget)}
              </div>
            )}
            {barAnalyticsWidgets.length > 0 && (
              <div className="device-analytics-bars-grid">
                {barAnalyticsWidgets.map(renderAnalyticsWidget)}
              </div>
            )}
            {shouldRenderTable && (() => {
              const orgName = userState?.organization?.name;
              const cleanOrgName = (orgName || "RouteEye").replace(/[^a-zA-Z0-9_-]/g, "_");
              const isSchoolApp = selectedApp === "schoolTrack";
              const deviceExportFileName = `${cleanOrgName}_${isSchoolApp ? "School_Devices" : "Devices"}`;

              const handleDownloadDevices = async () => {
                const downloadFileName = `${deviceExportFileName}.xlsx`;

                // 1. Get currently displayed devices (filtered or selected)
                const targetData = selectedDeviceIds.length > 0
                  ? filteredTableData.filter((row) =>
                      selectedDeviceIds.some((id) => String(id) === String(row.id || row.deviceId)),
                    )
                  : filteredTableData;

                const formatExportDate = (dateVal) => {
                  if (!dateVal) return "-";
                  try {
                    const d = new Date(dateVal);
                    if (isNaN(d.getTime())) return String(dateVal);
                    const day = String(d.getDate()).padStart(2, "0");
                    const month = String(d.getMonth() + 1).padStart(2, "0");
                    const year = d.getFullYear();
                    return `${day}/${month}/${year}`;
                  } catch {
                    return String(dateVal);
                  }
                };

                const formatExportSpecs = (specs) => {
                  if (!specs) return "-";
                  try {
                    if (Array.isArray(specs)) {
                      const formatted = specs
                        .filter((s) => s && (s.name || s.value))
                        .map((s) => `${s.name || ""}: ${s.value || ""}`)
                        .join(", ");
                      return formatted || "-";
                    }
                    if (typeof specs === "object") {
                      const formatted = Object.entries(specs)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(", ");
                      return formatted || "-";
                    }
                    return String(specs);
                  } catch {
                    return "-";
                  }
                };

                // 2. Map directly from the frontpage table data with all dropdown details
                const exportRows = targetData.map((row) => {
                  const dev = row.originalData || row;
                  const rawStatus = dev.status || row.status;
                  const isInstalled =
                    Boolean(dev.vehicle || dev.stop || dev.isInstalled || rawStatus === "INSTALLED" || rawStatus === "ACTIVE");
                  const rawCond = dev.workingCondition || row.workingCondition;
                  const workingCondition =
                    rawCond === "WORKING" || rawCond === "Working" || rawCond === "Yes"
                      ? "Working"
                      : rawCond === "NOT_WORKING" || rawCond === "Not Working" || rawCond === "NEEDS_REPAIR" || rawCond === "No"
                      ? "Not Working"
                      : rawCond || "-";

                  const sim = dev.simCard || dev.SimCard || {};
                  const simNumber =
                    row.simCardId || sim.simNumber || dev.simNumber || sim.mobileNumber || "-";
                  const simImei = sim.imeiNumber || sim.simImei || dev.simImei || "-";
                  const telecomOp = sim.provider || dev.provider || "-";
                  const simActivationDate = formatExportDate(sim.simActivationDate || sim.activationDate || dev.simActivationDate);
                  const nextRechargeDate = formatExportDate(sim.planExpiry || sim.nextRechargeDate || dev.nextRechargeDate);
                  const simOwner = sim.simOwner || sim.ownedBy || dev.simOwner || "-";
                  const simStatus =
                    String(sim.simStatus || sim.status || "").toUpperCase() === "ACTIVE"
                      ? "Active"
                      : sim.simStatus || sim.status
                      ? "Inactive"
                      : "-";

                  const installationDate = formatExportDate(dev.installationDate);
                  const orgNameDisplay =
                    dev.organizationName ||
                    dev.organization?.name ||
                    dev.Vehicle?.organization?.name ||
                    dev.assignedVehicle?.organization?.name ||
                    dev.Stop?.organization?.name ||
                    dev.assignedStop?.organization?.name ||
                    "-";
                  const cityDisplay =
                    dev.city ||
                    dev.organization?.city ||
                    dev.Vehicle?.organization?.city ||
                    dev.assignedVehicle?.organization?.city ||
                    dev.Stop?.organization?.city ||
                    dev.assignedStop?.organization?.city ||
                    "-";
                  const siteDisplay =
                    dev.siteName ||
                    dev.site?.name ||
                    dev.site?.siteName ||
                    dev.assignedVehicle?.site?.name ||
                    dev.assignedStop?.site?.name ||
                    "-";
                  const mountedOn =
                    dev.assignmentType === "STOP" || dev.stop || dev.Stop || dev.assignedStop
                      ? "Stop"
                      : "Vehicle";
                  const vehicleName =
                    dev.vehicleName ||
                    dev.vehicle?.name ||
                    dev.Vehicle?.name ||
                    dev.assignedVehicle?.name ||
                    dev.vehicle?.VehicleNumber ||
                    "-";
                  const vehicleReg =
                    dev.vehicleRegistrationNumber ||
                    dev.vehicle?.registrationNumber ||
                    dev.Vehicle?.registrationNumber ||
                    dev.assignedVehicle?.registrationNumber ||
                    "-";
                  const vehicleModel =
                    dev.vehicleModel ||
                    dev.vehicle?.model ||
                    dev.Vehicle?.model ||
                    dev.assignedVehicle?.model ||
                    "-";
                  const stopName =
                    dev.stopName ||
                    dev.stop?.name ||
                    dev.Stop?.name ||
                    dev.assignedStop?.name ||
                    "-";
                  const execName = dev.executiveFullName || dev.executiveName || "-";
                  const execPhone = dev.executivePhoneNumber || dev.executivePhone || "-";
                  const execEmail = dev.executiveEmail || "-";
                  const specsDisplay = formatExportSpecs(dev.specifications);

                  return {
                    "Device ID": row.deviceId || dev.deviceId || "-",
                    "Device Type": row.deviceType || dev.deviceType || "-",
                    "Status": row.status || getUiStatus(dev) || "-",
                    "Attached To": row.attachedTo || getAttachedTo(dev) || "-",
                    // Inner dropdown details (collapsible under [+] button in Excel):
                    "Owned By":
                      dev.ownedBy ||
                      dev.ownerOrganization?.name ||
                      dev.ownerOrgName ||
                      dev.organization?.name ||
                      "Irisidea",
                    "Device IMEI Number": dev.imeiNumber || row.imeiNumber || "-",
                    "Model": dev.model || "-",
                    "Vendor/Brand": dev.vendorBrand || dev.vendor || dev.brand || "-",
                    "Working Condition": workingCondition,
                    "Installation Status": isInstalled ? "Installed" : "Not Installed",
                    "Device Remarks": dev.remarks || dev.description || "-",
                    "SIM Number": simNumber,
                    "SIM IMEI / ICCID": simImei,
                    "Telecom Operator": telecomOp,
                    "SIM Activation Date": simActivationDate,
                    "Next Recharge Date": nextRechargeDate,
                    "SIM Owner": simOwner,
                    "SIM Status": simStatus,
                    "Installation Date": installationDate,
                    "Organization Name": orgNameDisplay,
                    "City": cityDisplay,
                    "Site": siteDisplay,
                    "Mounted On": mountedOn,
                    "Vehicle Name": vehicleName,
                    "Vehicle Registration Number": vehicleReg,
                    "Vehicle Model": vehicleModel,
                    "Stop Name": stopName,
                    "Executive Name": execName,
                    "Executive Phone": execPhone,
                    "Executive Email": execEmail,
                    "Specifications": specsDisplay,
                  };
                });

                try {
                  // 3. Send exact frontpage rows via POST to backend Excel export
                  await downloadBackendExcel({
                    url: `${config.baseURL}/excel/download`,
                    method: "POST",
                    data: {
                      table: "devices",
                      organizationId: currentOrganizationId,
                      format: "xlsx",
                      includeDetails: true,
                      rows: exportRows,
                    },
                    defaultFileName: downloadFileName,
                  });
                } catch (err) {
                  console.warn("Backend POST table=devices failed, trying table=device:", err);
                  try {
                    await downloadBackendExcel({
                      url: `${config.baseURL}/excel/download`,
                      method: "POST",
                      data: {
                        table: "device",
                        organizationId: currentOrganizationId,
                        format: "xlsx",
                        includeDetails: true,
                        rows: exportRows,
                      },
                      defaultFileName: downloadFileName,
                    });
                  } catch (err2) {
                    console.warn("Backend POST table=device failed, trying GET fallback:", err2);
                    try {
                      await downloadBackendExcel({
                        url: `${config.baseURL}/excel/download`,
                        method: "GET",
                        params: {
                          table: "device",
                          organizationId: currentOrganizationId,
                          format: "xlsx",
                          includeDetails: true,
                          ...(selectedDeviceIds && selectedDeviceIds.length > 0
                            ? { deviceIds: selectedDeviceIds.join(","), ids: selectedDeviceIds.join(",") }
                            : {}),
                        },
                        defaultFileName: downloadFileName,
                      });
                    } catch (fallbackErr) {
                      console.warn("Backend export not available, using client CSV fallback:", fallbackErr);
                      const detailedHeaders = [
                        { key: "Device ID", label: "Device ID" },
                        { key: "Device Type", label: "Device Type" },
                        { key: "Status", label: "Status" },
                        { key: "Attached To", label: "Attached To" },
                        { key: "Owned By", label: "Owned By" },
                        { key: "Device IMEI Number", label: "Device IMEI Number" },
                        { key: "Model", label: "Model" },
                        { key: "Vendor/Brand", label: "Vendor/Brand" },
                        { key: "Working Condition", label: "Working Condition" },
                        { key: "Installation Status", label: "Installation Status" },
                        { key: "Device Remarks", label: "Device Remarks" },
                        { key: "SIM Number", label: "SIM Number" },
                        { key: "SIM IMEI / ICCID", label: "SIM IMEI / ICCID" },
                        { key: "Telecom Operator", label: "Telecom Operator" },
                        { key: "SIM Activation Date", label: "SIM Activation Date" },
                        { key: "Next Recharge Date", label: "Next Recharge Date" },
                        { key: "SIM Owner", label: "SIM Owner" },
                        { key: "SIM Status", label: "SIM Status" },
                        { key: "Installation Date", label: "Installation Date" },
                        { key: "Organization Name", label: "Organization Name" },
                        { key: "City", label: "City" },
                        { key: "Site", label: "Site" },
                        { key: "Mounted On", label: "Mounted On" },
                        { key: "Vehicle Name", label: "Vehicle Name" },
                        { key: "Vehicle Registration Number", label: "Vehicle Registration Number" },
                        { key: "Vehicle Model", label: "Vehicle Model" },
                        { key: "Stop Name", label: "Stop Name" },
                        { key: "Executive Name", label: "Executive Name" },
                        { key: "Executive Phone", label: "Executive Phone" },
                        { key: "Executive Email", label: "Executive Email" },
                        { key: "Specifications", label: "Specifications" },
                      ];
                      exportTableToCSV(deviceExportFileName, detailedHeaders, exportRows);
                    }
                  }
                }
              };

              return (
                <div className="table_container device-table-container">
                  <DynamicTable
                    title={tableSection?.props?.title || "All Devices"}
                    headers={headers}
                    data={visibleTableData}
                    downloadData={filteredTableData}
                    downloadFileName={deviceExportFileName}
                    onDownload={handleDownloadDevices}
                    selectable={true}
                    selectedRowKeys={selectedDeviceIds}
                    onSelectionChange={setSelectedDeviceIds}
                    filters={tableFilters}
                    searchValue={searchTerm}
                    onSearchChange={handleSearchChange}
                    searchPlaceholder={tableSection?.props?.searchPlaceholder || "Search devices"}
                    headerVariant="dark"
                  minWidth={860}
                  rowKey={(row) => `${row.id || row.deviceId}`}
                  onRowClick={
                    isExpandableRowsEnabled ? (row) => toggleExpandedRow(row.id) : undefined
                  }
                  pagination={{
                    enabled: false,
                    pageSize: tableBatchSize,
                    itemLabel: "devices",
                  }}
                  renderActions={
                    isExpandableRowsEnabled
                      ? (row) => {
                          const isExpanded =
                            String(expandedDeviceId || "") === String(row.id || "");

                          return (
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleExpandedRow(row.id);
                              }}
                              sx={{ color: isExpanded ? "#2563EB" : "#64748B", p: 0 }}
                              aria-label={isExpanded ? t("Collapse details") : t("Expand details")}
                            >
                              {isExpanded ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          );
                        }
                      : undefined
                  }
                  renderExpandedRow={
                    isExpandableRowsEnabled
                      ? (row) => {
                          const isExpanded =
                            String(expandedDeviceId || "") === String(row.id || "");
                          if (!isExpanded) return null;

                          return (
                            <Box
                              sx={{
                                m: 1,
                                border: "none",
                                borderRadius: "10px",
                                backgroundColor: "#E8EAEE",
                                boxShadow: "none",
                              }}
                            >
                              <CreateDevice
                                embedded
                                embeddedDeviceId={row.id}
                                onCancel={() => setExpandedDeviceId(null)}
                                onComplete={() => {
                                  setExpandedDeviceId(null);
                                  fetchDevices();
                                }}
                                onDelete={() => handleInlineDeleteClick(row)}
                              />
                            </Box>
                          );
                        }
                      : undefined
                  }
                />
                {hasMoreDevices && (
                  <div className="device-show-more-footer">
                    <span>
                      {t("Showing")} {visibleTableData.length} {t("of")}{" "}
                      {filteredTableData.length} {t("devices")}
                    </span>
                    <button type="button" onClick={handleShowMoreDevices}>
                      {t("Show more")}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
          </>
        )}
      </div>

      <Box sx={{ height: "40px", backgroundColor: "#f4f4f5" }}></Box>
      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Device")}
        message={t(
          `Are you sure you want to delete ${deviceToDelete?.name || "this device"}?`,
        )}
        cancelButtonText={t("Cancel")}
        confirmButtonText={isDeleting ? t("Deleting...") : t("Delete")}
      />
    </>
  );
}


